/**
 * React Query hooks for Leads reporting endpoints.
 *
 * Reports are read-only aggregations — modeled as `useQuery` with
 * cached results so a tab switch doesn't re-aggregate.
 */

import { useQuery } from '@tanstack/react-query';
import api, {
  LeadsActivityTrendsResult,
  LeadsLostDealAnalysisResult,
  LeadsPipelineVelocityResult,
  LeadsSalesForecastResult,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/ui/use-toast';

export function usePipelineVelocity(pipelineId: string | null) {
  const query = useQuery<LeadsPipelineVelocityResult>({
    queryKey: queryKeys.leads.reports.pipelineVelocity(pipelineId ?? '_none'),
    queryFn: () => api.getLeadsPipelineVelocity(pipelineId as string),
    enabled: Boolean(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load pipeline velocity',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useSalesForecast(pipelineId?: string) {
  const query = useQuery<LeadsSalesForecastResult>({
    queryKey: queryKeys.leads.reports.forecast(pipelineId),
    queryFn: () => api.getLeadsSalesForecast(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load sales forecast',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useActivityTrends(period: 'week' | 'month' | 'quarter' = 'month') {
  const query = useQuery<LeadsActivityTrendsResult>({
    queryKey: queryKeys.leads.reports.activityTrends(period),
    queryFn: () => api.getLeadsActivityTrends(period),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load activity trends',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}

export function useLostDealAnalysis(pipelineId?: string) {
  const query = useQuery<LeadsLostDealAnalysisResult>({
    queryKey: queryKeys.leads.reports.lostDeals(pipelineId),
    queryFn: () => api.getLeadsLostDealAnalysis(pipelineId),
  });

  if (query.isError && !query.isFetching) {
    toast({
      title: 'Error',
      description: 'Failed to load lost deal analysis',
      variant: 'destructive',
    });
  }

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    refetch: () => query.refetch().then(() => undefined),
  };
}
